import React from 'react';
import ApplicantsCountChart from '../../components/Charts/ApplicantsCountChart';
import ApplicantsCompositionChart from '../../components/Charts/ApplicantsCompositionChart';
import { Box, Card, CardContent, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import HttpHelper from '../../utils/HttpHelper';
import './Dashboard.css';
import { formatAppDate } from '../../utils/dateFormat';

interface DashboardStats {
  totalApplicants: number;
  newApplicantsToday: number;
  newApplicantsThisWeek: number;
  interviewsScheduled: number;
  offersMade: number;
  ungradedApplications: number;
  dailyCounts: number[];
  composition: { status: string; count: number }[];
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = React.useState<DashboardStats | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await HttpHelper.get('/metrics/dashboard-stats');
        setStats(response);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading || !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  const labels = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    labels.push(
      formatAppDate(d, '')
    );
  }

  return (
    <div className="dashboard">
      <Paper sx={{ p: 2, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {/* Main Chart */}
          <Grid item xs={12} md={7}>
            <Typography variant="h6" gutterBottom>
              Applications Trend (Last 7 Days)
            </Typography>
            <ApplicantsCountChart data={stats.dailyCounts} labels={labels} />
          </Grid>

          {/* Side Charts & Stats */}
          <Grid item container direction={'column'} xs={12} md={5} spacing={2}>
            <Grid item sx={{ maxHeight: 300 }}>
              <Typography variant="h6" gutterBottom>
                Applicant Composition
              </Typography>
              <ApplicantsCompositionChart data={stats.composition} />
            </Grid>

            {/* Metric Cards */}
            <Grid item container spacing={1}>
              <Grid item xs={6}>
                <Card sx={{ bgcolor: '#f5f5f5' }}>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Total Applicants
                    </Typography>
                    <Typography variant="h6">{stats.totalApplicants}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ bgcolor: '#e3f2fd' }}>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" color="text.secondary">
                      New (Today/Week)
                    </Typography>
                    <Typography variant="h6">
                      {stats.newApplicantsToday} / {stats.newApplicantsThisWeek}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ bgcolor: '#f1f8e9' }}>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Scheduled Interviews
                    </Typography>
                    <Typography variant="h6">
                      {stats.interviewsScheduled}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ bgcolor: '#fffde7' }}>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Offers Made
                    </Typography>
                    <Typography variant="h6">{stats.offersMade}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#fff3e0' }}>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Ungraded Applications
                    </Typography>
                    <Typography variant="h6" color="error">
                      {stats.ungradedApplications}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default Dashboard;
